import type { Ref } from "vue";
import { SearchMode } from "~/core/composables/useSearch";
import LxcError from "~/core/models/LxcError.class";
import type { OperationModel } from "~/core/models/OperationModel.interface";
import type { OperationModelType } from "~/core/models/OperationModelType.enum";
import { Filters } from "~/core/models/filters";
import { checkIsOperationModelEnabled } from "~/core/utils/checkIsOperationModelEnabled";
import { versionCompare } from "~/core/utils/versionCompare";
import { useFetchDtwinOperationModels } from "~/modules/dtwin/composables/useFetchDtwinOperationModels";
import type { DtwinModel } from "~/modules/dtwin/models/DtwinModel.interface";

interface UseFetchDtwinOperationModelParams {
  organizationCode?: number;
  operationType: OperationModelType;
}

/**
 * Composable to fetch the latest dtwin operation model for a given device type and operation type.
 * Automatically checks if the operation model is enabled based on license features.
 */
export function useFetchDtwinOperationModel({
  organizationCode,
  operationType,
}: UseFetchDtwinOperationModelParams) {
  const {
    isLoading,
    error: fetchError,
    fetchData,
    results,
    setFilter,
  } = useFetchDtwinOperationModels({
    organizationCode,
    searchMode: SearchMode.FILTER_SEARCH,
  });

  const dtwinOperationModel: Ref<OperationModel | undefined> = ref();
  const error: Ref<LxcError | undefined> = ref();

  /**
   * Computed property to check if the operation model is enabled based on license features.
   * Returns true if:
   * - No enabledLicenceFeature is defined on the operation model, OR
   * - The feature is enabled in the current license
   */
  const isOperationModelEnabled = computed(() => {
    return checkIsOperationModelEnabled(dtwinOperationModel.value);
  });

  /**
   * Fetches the dtwin operation model for the given device type and operation type.
   * Sorts by version (descending) and returns the latest version.
   *
   * * @param dtwinModel - The device twin model to fetch the operation model
   */
  async function fetchDtwinOperationModel(dtwinModel?: DtwinModel) {
    if (dtwinModel) {
      error.value = undefined;

      setFilter(Filters.DTWIN_OPERATION_MODEL_TYPE, operationType);
      setFilter(Filters.DTWIN_OPERATION_MODEL_DEVICE_TYPE, [dtwinModel.name]);
      setFilter(Filters.DTWIN_OPERATION_MODEL_HIDDEN, "false");

      await fetchData();

      if (LxcError.check(fetchError.value)) {
        error.value = fetchError.value;
        dtwinOperationModel.value = undefined;
      } else {
        const dtwinOperationModels = results.value?.results;

        // Sort the operation models by version (descending) and get the latest one
        dtwinOperationModel.value = dtwinOperationModels?.length
          ? dtwinOperationModels.sort((model1, model2) =>
              versionCompare(model2.version, model1.version),
            )[0]
          : undefined;
      }
    }
  }

  return {
    dtwinOperationModel,
    isOperationModelEnabled,
    isLoading,
    error,
    fetchDtwinOperationModel,
  };
}
